import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { MdContentCopy, MdOpenInNew } from 'react-icons/md';
import { Button, EmptyState } from '@aibuild/ui';
import { canDuplicate } from '../appPermissions';
import { useDuplicateAndOpen } from '../useDuplicateAndOpen';
import { useAppCatalog } from '../useApps';
import { StateActions, StateWrap } from './BuilderUnavailable.styled';

interface BuilderUnavailableProps {
  readonly appKey: string | undefined;
}

const BuilderUnavailable: React.FC<BuilderUnavailableProps> = ({ appKey }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { data: catalog } = useAppCatalog();
  const { duplicateAndOpen, isDuplicating } = useDuplicateAndOpen();
  const shared = catalog?.find((entry) => entry.appKey === appKey && !entry.ownedByMe && entry.visibility === 'ORG');

  return (
    <StateWrap data-testid="app-builder__unavailable">
      <EmptyState
        title={intl.formatMessage({ id: 'apps.builder.missing.title', defaultMessage: 'This app is not available' })}
        description={
          shared
            ? intl.formatMessage({
                id: 'apps.builder.missing.shared',
                defaultMessage: 'Only its creator can edit it. You can open it, or make your own copy to change.',
              })
            : intl.formatMessage({
                id: 'apps.builder.missing.body',
                defaultMessage: 'It may have been deleted, or it belongs to someone else.',
              })
        }
      />
      <StateActions>
        {shared && (
          <Button
            variant="filled"
            leadingIcon={<MdOpenInNew size={16} aria-hidden="true" />}
            onClick={() => navigate(`/apps/${encodeURIComponent(shared.appKey)}`)}
            dataTestId="app-builder__open-app"
          >
            {intl.formatMessage({ id: 'apps.builder.missing.open', defaultMessage: 'Open app' })}
          </Button>
        )}
        {shared && canDuplicate(shared) && (
          <Button
            variant="outlined"
            leadingIcon={<MdContentCopy size={16} aria-hidden="true" />}
            loading={isDuplicating}
            onClick={() => duplicateAndOpen(shared.appKey)}
            dataTestId="app-builder__duplicate"
          >
            {intl.formatMessage({ id: 'apps.builder.missing.duplicate', defaultMessage: 'Duplicate' })}
          </Button>
        )}
        <Button variant={shared ? 'text' : 'outlined'} onClick={() => navigate('/apps')} dataTestId="app-builder__back-to-apps">
          {intl.formatMessage({ id: 'apps.builder.back', defaultMessage: 'Back to apps' })}
        </Button>
      </StateActions>
    </StateWrap>
  );
};

export default BuilderUnavailable;
